import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Plus, Edit, Trash2, Eye, Play, Pause, MoreHorizontal } from 'lucide-react';
import { adminService } from '../services/adminService';
import toast from 'react-hot-toast';

const Campaigns = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const navigate = useNavigate();

  // Fetch campaigns on component mount
  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const data = await adminService.getCampaigns();
      if (data && data.campaigns && Array.isArray(data.campaigns)) {
        setCampaigns(data.campaigns);
        setError(null);
      } else if (Array.isArray(data)) {
        setCampaigns(data);
        setError(null);
      } else {
        setCampaigns([]);
        setError('Invalid data format received from server');
        toast.error('Invalid data format received');
      }
    } catch (err) {
      setCampaigns([]);
      setError(err.message);
      toast.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  // Only use API data, show empty state if no campaigns
  const displayCampaigns = Array.isArray(campaigns) ? campaigns : [];

  let filteredCampaigns = displayCampaigns.filter(campaign => {
    const matchesSearch = (campaign.title || campaign.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || campaign.status === selectedStatus;
    return matchesSearch && matchesStatus;
  });

  // Add a dummy campaign if there are no campaigns and not loading/error
  const isDummy = !loading && !error && filteredCampaigns.length === 0;
  if (isDummy) {
    filteredCampaigns = [{
      id: 'dummy',
      title: 'Demo Campaign',
      status: 'draft',
      type: 'promotional',
      start_date: new Date().toISOString(),
      end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      budget: 1000,
      applications_count: 0,
      applications_accepted: 0,
      applications_pending: 0,
      applications_rejected: 0,
      description: 'This is a demo campaign for preview purposes.',
    }];
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: 'bg-green-100 text-green-800',
      draft: 'bg-gray-100 text-gray-800',
      paused: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-blue-100 text-blue-800',
      flagged: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig[status] || statusConfig.draft}`}>
        {status || 'draft'}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      promotional: 'bg-purple-100 text-purple-800',
      seasonal: 'bg-orange-100 text-orange-800',
      onboarding: 'bg-blue-100 text-blue-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeConfig[type] || typeConfig.promotional}`}>
        {type}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    // Use Indian Rupee symbol and formatting
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Action handlers
  const handleView = (campaign) => {
    setSelectedCampaign(campaign);
    setShowModal(true);
  };

  const handleEdit = (campaign) => {
    if (campaign.id === 'dummy') {
      toast('Demo campaign: edit not available');
      return;
    }
    navigate(`/admin/campaigns/${campaign.id}/edit`);
  };

  const handleDelete = async (campaign) => {
    if (campaign.id === 'dummy') {
      toast('Demo campaign: delete not available');
      return;
    }
    if (window.confirm(`Delete campaign "${campaign.title}"?`)) {
      try {
        await adminService.deleteCampaign(campaign.id);
        toast.success('Campaign deleted');
        fetchCampaigns();
      } catch (err) {
        toast.error('Failed to delete campaign');
      }
    }
  };

  const handleActivate = async (campaign) => {
    if (campaign.id === 'dummy') {
      toast('Demo campaign: activate not available');
      return;
    }
    // Example: Call API to activate campaign (implement if available)
    toast.success(`Campaign "${campaign.title}" activated`);
    // fetchCampaigns(); // Uncomment if backend supports status change
  };

  const handlePause = async (campaign) => {
    if (campaign.id === 'dummy') {
      toast('Demo campaign: pause not available');
      return;
    }
    // Example: Call API to pause campaign (implement if available)
    toast.success(`Campaign "${campaign.title}" paused`);
    // fetchCampaigns(); // Uncomment if backend supports status change
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCampaign(null);
  };

  const openImageModal = (e) => {
    e.stopPropagation();
    setShowImageModal(true);
  };
  const closeImageModal = () => setShowImageModal(false);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
          <p className="text-gray-600">
            {error ? 'Failed to load campaigns' : 'Manage your marketing campaigns'}
          </p>
        </div>
        <button className="btn-primary flex items-center" onClick={() => navigate('/admin/campaigns/create')}>
          <Plus className="h-4 w-4 mr-2" />
          Create Campaign
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="card p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading campaigns...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="card p-6 mb-6">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={fetchCampaigns} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="input-field"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="flagged">Flagged</option>
            </select>
            <button className="btn-secondary flex items-center">
              <Filter className="h-4 w-4 mr-2" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Campaigns Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredCampaigns.map((campaign) => (
          <div key={campaign.id} className="card p-6 hover:shadow-lg transition-shadow">
            {/* Campaign Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{campaign.title || campaign.name || 'Untitled'}</h3>
                <div className="flex items-center gap-2 mb-2">
                  {getStatusBadge(campaign.status)}
                  {getTypeBadge(campaign.type || 'Standard')}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button className="p-1 text-gray-400 hover:text-gray-600" onClick={() => handleView(campaign)}>
                  <Eye className="h-4 w-4" />
                </button>
                <button className="p-1 text-gray-400 hover:text-gray-600" onClick={() => handleEdit(campaign)}>
                  <Edit className="h-4 w-4" />
                </button>
                <button className="p-1 text-gray-400 hover:text-gray-600" onClick={() => handleDelete(campaign)}>
                  <Trash2 className="h-4 w-4" />
                </button>
                <button className="p-1 text-gray-400 hover:text-gray-600">
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Campaign Details */}
            <div className="space-y-3 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Duration:</span>
                <span className="text-gray-900">
                  {campaign.start_date ? new Date(campaign.start_date).toLocaleDateString() : 'N/A'} - 
                  {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Budget:</span>
                <span className="text-gray-900">{campaign.budget ? formatCurrency(campaign.budget) : 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Applications:</span>
                <span className="text-gray-900">{campaign.applications_count || 0}</span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-500">Application Status</span>
                <span className="text-gray-900">
                  {campaign.applications_accepted || 0} / {campaign.applications_count || 0}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${campaign.applications_count ? (campaign.applications_accepted / campaign.applications_count) * 100 : 0}%` 
                  }}
                ></div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="grid grid-cols-3 gap-4 text-center border-t pt-4">
              <div>
                <div className="text-lg font-semibold text-gray-900">{campaign.applications_pending || 0}</div>
                <div className="text-xs text-gray-500">Pending</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">{campaign.applications_accepted || 0}</div>
                <div className="text-xs text-gray-500">Accepted</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">{campaign.applications_rejected || 0}</div>
                <div className="text-xs text-gray-500">Rejected</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 mt-4 pt-4 border-t">
              {campaign.status === 'active' ? (
                <button
                  className="btn-secondary flex-1 flex items-center justify-center"
                  onClick={() => handlePause(campaign)}
                  disabled={campaign.id === 'dummy'}
                >
                  <Pause className="h-4 w-4 mr-2" />
                  Pause
                </button>
              ) : (
                <button
                  className="btn-primary flex-1 flex items-center justify-center"
                  onClick={() => handleActivate(campaign)}
                  disabled={campaign.id === 'dummy'}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Activate
                </button>
              )}
              <button
                className="btn-secondary flex items-center justify-center px-3"
                onClick={() => handleEdit(campaign)}
                disabled={campaign.id === 'dummy'}
              >
                <Edit className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* View Campaign Modal */}
      {showModal && selectedCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-3xl min-h-[600px] max-h-[90vh] p-8 relative overflow-y-auto"
            style={{ boxSizing: 'border-box' }}
          >
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              onClick={closeModal}
              aria-label="Close"
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-6">Campaign Details</h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4 mb-6">
              <div>
                <div className="mb-2 text-sm text-gray-500">Title</div>
                <div className="font-medium text-gray-900">{selectedCampaign.title || selectedCampaign.name || '-'}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Status</div>
                {getStatusBadge(selectedCampaign.status)}
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Budget</div>
                <div className="font-medium text-gray-900">{formatCurrency(selectedCampaign.budget)}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Brand Name</div>
                <div className="font-medium text-gray-900">{selectedCampaign.brand_name || selectedCampaign.brand?.name || '-'}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Brand ID</div>
                <div className="font-medium text-gray-900">{selectedCampaign.brand_id || '-'}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Start Date</div>
                <div className="font-medium text-gray-900">{selectedCampaign.start_date ? new Date(selectedCampaign.start_date).toLocaleString() : '-'}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">End Date</div>
                <div className="font-medium text-gray-900">{selectedCampaign.end_date ? new Date(selectedCampaign.end_date).toLocaleString() : '-'}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Created At</div>
                <div className="font-medium text-gray-900">{selectedCampaign.created_at ? new Date(selectedCampaign.created_at).toLocaleString() : '-'}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Updated At</div>
                <div className="font-medium text-gray-900">{selectedCampaign.updated_at ? new Date(selectedCampaign.updated_at).toLocaleString() : '-'}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Applications</div>
                <div className="font-medium text-gray-900">{selectedCampaign.applications_count || 0}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Accepted</div>
                <div className="font-medium text-gray-900">{selectedCampaign.applications_accepted || 0}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Pending</div>
                <div className="font-medium text-gray-900">{selectedCampaign.applications_pending || 0}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Rejected</div>
                <div className="font-medium text-gray-900">{selectedCampaign.applications_rejected || 0}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Coins Deducted</div>
                <div className="font-medium text-gray-900">{selectedCampaign.coins_deducted || '-'}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Flag Reason</div>
                <div className="font-medium text-gray-900">{selectedCampaign.flag_reason || '-'}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Min Followers</div>
                <div className="font-medium text-gray-900">{selectedCampaign.minFollowers || '-'}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Location</div>
                <div className="font-medium text-gray-900">{selectedCampaign.location || '-'}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Categories</div>
                <div className="font-medium text-gray-900">
                  {(() => {
                    if (!selectedCampaign.categories) return '-';
                    try {
                      const cats = typeof selectedCampaign.categories === 'string' ? JSON.parse(selectedCampaign.categories) : selectedCampaign.categories;
                      if (Array.isArray(cats)) return cats.join(', ');
                      return String(cats);
                    } catch {
                      return String(selectedCampaign.categories);
                    }
                  })()}
                </div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Gender</div>
                <div className="font-medium text-gray-900">{selectedCampaign.gender || '-'}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">Language</div>
                <div className="font-medium text-gray-900">{selectedCampaign.language || '-'}</div>
              </div>
              <div>
                <div className="mb-2 text-sm text-gray-500">No. of Influencers</div>
                <div className="font-medium text-gray-900">{selectedCampaign.number_of_influencers || '-'}</div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="font-semibold mb-2">Description</div>
              <div className="text-gray-700">{selectedCampaign.description || '-'}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="font-semibold mb-2">Goals</div>
              <div className="text-gray-700">
                {(() => {
                  if (!selectedCampaign.goals) return '-';
                  try {
                    const goalsObj = typeof selectedCampaign.goals === 'string' ? JSON.parse(selectedCampaign.goals) : selectedCampaign.goals;
                    if (typeof goalsObj === 'object' && goalsObj !== null) {
                      return Object.entries(goalsObj).map(([k, v]) => `${k}: ${v}`).join(', ');
                    }
                    return String(goalsObj);
                  } catch {
                    return String(selectedCampaign.goals);
                  }
                })()}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="font-semibold mb-2 flex items-center justify-between">
                <span>Image</span>
                {selectedCampaign.image_url && (
                  <button
                    className="text-blue-600 underline text-sm ml-2"
                    onClick={openImageModal}
                    type="button"
                  >
                    View Full Screen
                  </button>
                )}
              </div>
              {selectedCampaign.image_url ? (
                <div className="flex items-center justify-center">
                  <img
                    src={selectedCampaign.image_url}
                    alt="Campaign"
                    style={{
                      maxWidth: '320px',
                      maxHeight: '180px',
                      width: 'auto',
                      height: 'auto',
                      borderRadius: '0.5rem',
                      objectFit: 'contain',
                      background: '#f3f4f6'
                    }}
                  />
                </div>
              ) : (
                <div className="text-gray-700 text-center py-8">No image available</div>
              )}
            </div>

            {/* Full Screen Image Modal */}
            {showImageModal && selectedCampaign.image_url && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80" onClick={closeImageModal}>
                <div className="relative max-w-4xl w-full flex flex-col items-center">
                  <button
                    className="absolute top-4 right-4 text-white text-3xl font-bold z-10"
                    onClick={closeImageModal}
                    aria-label="Close"
                    type="button"
                  >
                    &times;
                  </button>
                  <img
                    src={selectedCampaign.image_url}
                    alt="Campaign Full Screen"
                    style={{
                      maxWidth: '90vw',
                      maxHeight: '80vh',
                      width: 'auto',
                      height: 'auto',
                      borderRadius: '0.5rem',
                      objectFit: 'contain',
                      background: '#f3f4f6',
                      boxShadow: '0 0 24px 0 rgba(0,0,0,0.5)'
                    }}
                  />
                </div>
              </div>
            )}
            {selectedCampaign.brief_url && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="font-semibold mb-2">Brief</div>
                <a href={selectedCampaign.brief_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                  {selectedCampaign.brief_url}
                </a>
              </div>
            )}
            <button className="btn-primary mt-2 w-full" onClick={closeModal}>Close</button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredCampaigns.length === 0 && (
        <div className="card p-12 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
            <Filter className="h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || selectedStatus !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by creating your first campaign.'
            }
          </p>
          <button className="btn-primary" onClick={() => navigate('/admin/campaigns/create')}>
            <Plus className="h-4 w-4 mr-2" />
            Create Campaign
          </button>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
